import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { EntlastungUebersicht } from '../../models/entlastung.model';
import { EntlastungenService } from '../../services/entlastungen.service';

type StatusFilter = 'ALLE' | 'ERFASST' | 'OFFEN';

@Component({
  selector: 'app-entlastungen-uebersicht',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './entlastungen-uebersicht.component.html',
  styleUrl: './entlastungen-uebersicht.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntlastungenUebersichtComponent {
  private readonly entlastungenService = inject(EntlastungenService);
  private readonly router = inject(Router);

  protected readonly entries = signal<EntlastungUebersicht[]>([]);
  protected readonly selectedFilter = signal<StatusFilter>('ALLE');
  protected readonly isLoading = signal(true);
  protected readonly skeletonSummaryCards = Array.from({ length: 3 });
  protected readonly skeletonRows = Array.from({ length: 7 });

  protected readonly heuteLabel = this.formatDate(this.todayIso());

  protected readonly erfasstCount = computed(
    () => this.entries().filter((item) => item.status === 'Erfasst').length
  );

  protected readonly offenCount = computed(
    () => this.entries().filter((item) => item.status === 'Offen').length
  );

  protected readonly gesamtMenge = computed(() =>
    this.entries().reduce((sum, item) => sum + item.gesamtMenge, 0)
  );

  protected readonly filteredEntries = computed(() => {
    const filter = this.selectedFilter();
    const rows = this.entries().filter((item) => {
      if (filter === 'ALLE') {
        return true;
      }

      return item.status === (filter === 'ERFASST' ? 'Erfasst' : 'Offen');
    });

    return [...rows].sort((first, second) => {
      if (first.anzahlPositionen !== second.anzahlPositionen) {
        return second.anzahlPositionen - first.anzahlPositionen;
      }

      return first.filialName.localeCompare(second.filialName, 'de');
    });
  });

  constructor() {
    this.loadOverview();
  }

  protected setFilter(filter: StatusFilter): void {
    this.selectedFilter.set(filter);
  }

  protected isFilterActive(filter: StatusFilter): boolean {
    return this.selectedFilter() === filter;
  }

  protected openEntlastung(entry: EntlastungUebersicht): void {
    void this.router.navigate(['/entlastungen', entry.filialNummer]);
  }

  protected formatMenge(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  private loadOverview(): void {
    this.isLoading.set(true);

    this.entlastungenService
      .getOverview()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (entries) => this.entries.set(entries),
        error: () => this.entries.set([]),
      });
  }

  private todayIso(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private formatDate(value: string): string {
    const [year, month, day] = value.split('-');

    return `${day}.${month}.${year}`;
  }
}
